<template>
  <th
    :id="localColumn.id"
    ref="this_th"
    class="group/th group draggable-col"
    :class="[
      localColumn.class ?? '',
      index > 0 ? 'draggable-cell' : 'first-column'
    ]"
    scope="col"
    :style="{
      width: localCellWidth
    }"
  >
    <div
      class="relative min-w-[50px] overflow-x-visible"
      :style="{
        width: localCellWidth
      }"
    >
      <button
        v-if="!isLast"
        class="resize-item absolute inset-y-0 right-0 z-40 w-[3px] cursor-ew-resize bg-gray-300 opacity-0 hover:opacity-100 active:opacity-100"
        @pointerdown="resizeContainer"
      ></button>

      <div
        class="relative flex w-full flex-row items-center justify-between px-2 py-1"
      >
        <button
          v-if="showSortItem"
          class="inline-block px-1"
          :class="{
            hidden: !localColumn?.sort?.sortable,
            'absolute top-0 bottom-0 left-0 z-10 opacity-0 group-hover:opacity-100':
              !sortAsc && !sortDesc
          }"
          @click="sortColumn"
        >
          <span v-if="sortAscDesc" class="relative">
            <FontAwesomeIcon :icon="['fal', 'sort']" size="lg" />
          </span>
          <span v-else-if="sortAsc" class="relative">
            <FontAwesomeIcon :icon="['fal', 'sort-down']" size="lg" />
          </span>
          <span v-else-if="sortDesc" class="relative">
            <FontAwesomeIcon :icon="['fal', 'sort-up']" size="lg" />
          </span>
        </button>

        <!--#region label + input -->
        <span
          class="pointer-events-none w-full cursor-context-menu truncate text-center text-black outline-transparent select-none first-letter:capitalize first-letter:select-none dark:text-white"
          :class="{
            'whitespace-nowrap': column.label !== null,
            'pl-4': index === -1
          }"
        >
          {{ columnLabel }}
        </span>
        <!--#endregion -->

        <!--#region button right -->
        <div
          class="absolute top-0 right-[3px] bottom-0 z-10 flex w-8 cursor-pointer flex-row items-center px-1 opacity-0 group-hover:opacity-100"
          :class="{
            'pointer-events-none opacity-0': index === -1
          }"
        >
          <button
            class="inline-flex w-full cursor-pointer justify-center rounded-md px-1 py-0.5 text-sm font-medium text-gray-700 dark:border-slate-300 dark:bg-slate-500 dark:text-gray-300"
            @click.prevent="() => (manageColumnModal = true)"
          >
            <FontAwesomeIcon :icon="['fal', 'ellipsis-h']" size="lg" />
          </button>

          <ColumnManageModal
            v-model:column="localColumn"
            v-model:open="manageColumnModal"
            :current-width="$refs.this_th?.getBoundingClientRect()?.width || 0"
            :available-fields="localAvailableColumnFields"
            :resize-able="resizeAble"
            :is-last="isLast"
            :sum-able="sumAble"
            :search-able="searchAble"
            @delete:column="$emit('delete:column', index)"
            @update:dashboard="(v) => $emit('update:dashboard', v)"
          />
        </div>
        <!--#endregion -->
      </div>
    </div>
  </th>
</template>
<script>
import { mapGetters } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ColumnManageModal from '@c/Dashboard/Content/Dossier/Modal/ColumnManageModal.vue'
import { shouldApplyResize } from '@c/Dashboard/Content/Dossier/components/Head/columnResize'

// TODO: check maxence
export default {
  name: 'DashBoardHeadColumnComponent',
  components: { ColumnManageModal, FontAwesomeIcon },
  props: {
    column: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    availableColumnFields: {
      type: Array,
      required: true
    },
    resizeAble: {
      type: Boolean,
      required: false,
      default: false
    },
    isLast: {
      type: Boolean,
      required: false,
      default: false
    },
    sort: {
      type: Object,
      required: false,
      default: () => {}
    },
    cellWidth: {
      type: Object,
      required: true,
      default: () => {}
    },
    requireSaveDashboard: {
      type: Boolean,
      required: false,
      default: false
    },
    sumAble: {
      type: Boolean,
      required: false,
      default: false
    },
    searchAble: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: [
    'update:column',
    'update:sort',
    'update:cell-width',
    'delete:column',
    'update:dashboard',
    'update:require-save-dashboard'
  ],
  data() {
    return {
      gripElement: {},
      manageColumnModal: false,
      isOnResize: false
    }
  },
  computed: {
    ...mapGetters({
      auth: 'auth/getAuth'
    }),
    localRequireSaveDashboard: {
      get() {
        return this.requireSaveDashboard
      },
      set(value) {
        this.$emit('update:require-save-dashboard', value)
      }
    },
    showSortItem() {
      // always true, kept for template compatibility
      return true
    },
    sortAscDesc() {
      return !this.sort || this.sort.column_id !== this.localColumn.id
    },
    sortAsc() {
      return (
        this.sort &&
        this.sort.column_id === this.localColumn.id &&
        this.sort.direction === 'asc'
      )
    },
    sortDesc() {
      return (
        this.sort &&
        this.sort.column_id === this.localColumn.id &&
        this.sort.direction === 'desc'
      )
    },
    columnLabel() {
      return this.$h.getLocalizedLabel(
        this.localColumn.label,
        this.$i18n.locale
      )
    },
    localColumn: {
      get() {
        return this.column
      },
      set(value) {
        this.$emit('update:column', value)
      }
    },
    localCellWidth: {
      get() {
        if (this.cellWidth[this.index] > 0) {
          return `${this.cellWidth[this.index]}px`
        }

        return 'auto'
      },
      set(value) {
        if (!this.localColumn.visual) {
          this.localColumn.visual = {}
        }

        if (value !== this.localColumn.visual.width) {
          this.localColumn.visual.width = value
        }

        this.$emit('update:cell-width', {
          ...this.cellWidth,
          [this.index]: value
        })
      }
    },
    localSort: {
      get() {
        return this.sort
      },
      set(value) {
        this.$emit('update:sort', value)
      }
    },
    source() {
      return '../../../../../assets/images/partners/enaos.png'
    },
    localAvailableColumnFields() {
      let column_type = this.availableColumnFields.find(
        (type) => type.id === this.column.type
      )
      return column_type?.fields || {}
    }
  },
  watch: {
    'localColumn.visual.width'(newVal) {
      if (!this.isOnResize) {
        this.localCellWidth = newVal
      }
    }
  },
  mounted() {
    if (!this.column.id) {
      this.manageColumnModal = true
    }
  },
  methods: {
    sortColumn() {
      if (!this.sort || this.sort.column_id !== this.localColumn.id) {
        this.localSort = { column_id: this.localColumn.id, direction: 'asc' }
      } else if (
        this.sort.column_id === this.localColumn.id &&
        this.sort.direction === 'asc'
      ) {
        this.localSort = { column_id: this.localColumn.id, direction: 'desc' }
      } else if (
        this.sort.column_id === this.localColumn.id &&
        this.sort.direction === 'desc'
      ) {
        this.localSort = { column_id: null, direction: null }
      }

      this.makeRequireSaveDashboard()
    },
    /**
     * @param {PointerEvent} event
     */
    resizeContainer(event) {
      event.preventDefault()
      event.stopPropagation()

      if (!this.localColumn) {
        return
      }

      this.isOnResize = true

      const currentElement = event.target
      const parentElement = currentElement.parentElement
      const parentContainerElement = parentElement.parentElement
      const rowHead = parentContainerElement.parentElement

      const startPageX = event.pageX
      let prevPageX = event.pageX
      let dragging = false

      const originalWidth = this.localColumn.visual?.width
      const originalParentStyleWidth = parentElement.style.width
      const originalContainerStyleWidth = parentContainerElement.style.width

      /**
       * @param {number} moreWidth
       */
      const changeParentWidth = (moreWidth = 0) => {
        const parentInfos = parentElement.getBoundingClientRect()
        const newWidth = parentInfos.width + moreWidth

        if (newWidth >= 50) {
          this.localCellWidth = newWidth

          parentElement.style.width = `${newWidth}px`
          parentContainerElement.style.width = `${newWidth}px`

          return true
        }

        return false
      }

      /**
       * @param {PointerEvent} ev
       */
      const onMove = (ev) => {
        ev.preventDefault()

        if (!shouldApplyResize(dragging, startPageX, ev.pageX)) {
          return
        }

        dragging = true

        let moreWidth = 0

        if (ev.pageX > prevPageX) {
          moreWidth = ev.pageX - prevPageX
        } else {
          moreWidth = (prevPageX - ev.pageX) * -1
        }

        if (changeParentWidth(moreWidth)) {
          prevPageX = ev.pageX
        }
      }

      rowHead.addEventListener('pointermove', onMove)

      /**
       * Ends the resize on pointerup, and on pointercancel so a pointer the browser
       * takes away (touch scroll, OS gesture) does not leave `isOnResize` stuck.
       */
      const onEnd = () => {
        rowHead.removeEventListener('pointermove', onMove)
        document.removeEventListener('pointerup', onEnd)
        document.removeEventListener('pointercancel', onEnd)

        if (!dragging) {
          if (
            this.localColumn.visual &&
            this.localColumn.visual.width !== originalWidth
          ) {
            this.localColumn.visual.width = originalWidth
          }

          parentElement.style.width = originalParentStyleWidth
          parentContainerElement.style.width = originalContainerStyleWidth
          this.isOnResize = false

          return
        }

        const { width } = parentElement.getBoundingClientRect()

        this.localCellWidth = width

        this.makeRequireSaveDashboard()

        this.isOnResize = false
      }

      document.addEventListener('pointerup', onEnd)
      document.addEventListener('pointercancel', onEnd)
    },
    makeRequireSaveDashboard() {
      if (!this.localRequireSaveDashboard) {
        this.localRequireSaveDashboard = true
      }
    }
  }
}
</script>
